import type { AuditEvent, ListAuditEventsParams } from '../api/client';
import { t } from '../i18n';

export const AUDIT_LIST_PAGE_SIZE = 50;
export const AUDIT_LOAD_ALL_PAGE_SIZE = 200;
export const AUDIT_DOM_CAP = 5_000;
export const AUDIT_CSV_CAP = 10_000;

export const AUDIT_MODULES = ['all', 'sedr', 'files', 'messages', 'vault', 'sms', 'system'] as const;
export type AuditModule = (typeof AUDIT_MODULES)[number];

export const AUDIT_EVENT_GROUPS: { module: Exclude<AuditModule, 'all'>; eventTypes: string[] }[] = [
  {
    module: 'vault',
    eventTypes: [
      'RecoveryPackageCreated',
      'RecoveryPackageReplaced',
      'RecoveryPackageAdminInvalidated',
      'VaultPassphraseChanged',
    ],
  },
  {
    module: 'messages',
    eventTypes: [
      'InfoShareCreated',
      'InfoShareRevoked',
      'InfoSharePurged',
      'InfoShareReplyPurged',
      'GuestVerifyLockedOut',
    ],
  },
  {
    module: 'files',
    eventTypes: [
      'SharedFolderCreated',
      'SharedFolderRevoked',
      'SharedFolderExpiryExtended',
      'SharedFolderMemberInvited',
      'SharedFolderMemberPermissionChanged',
      'SharedFolderMemberRemoved',
      'SharedFolderGuestInviteCreated',
      'SharedFolderGuestInvitePermissionChanged',
      'SharedFolderGuestInviteRevoked',
      'SharedFolderManifestCommitted',
      'SharedFolderGuestVerified',
      'GuestVerifyLockedOut',
      'SharedFolderPurged',
      'SharedFolderExpiryWarningSent',
      'SharedFolderExpiryDigestSent',
      'SharedFolderDeletedFilePurged',
      'SharedFolderManifestHistoryPurged',
    ],
  },
  {
    module: 'sms',
    eventTypes: ['SmsSent', 'SmsSendFailed', 'SmsDeliveryFailed', 'CmComBudgetAlertSent', 'CmComBudgetAlertFailed'],
  },
  {
    module: 'system',
    eventTypes: ['SystemSettingsUpdated', 'VersionUpdated', 'VersionReportingFailed'],
  },
  {
    module: 'sedr',
    eventTypes: [
      'SedrRoomCreated',
      'SedrMemberInvited',
      'SedrMemberRemoved',
      'SedrGuestInviteCreated',
      'SedrGuestInviteRevoked',
      'SedrGuestVerified',
      'SedrRequirementsImported',
      'SedrRoomDeletionScheduled',
      'SedrRoomDeletionCancelled',
      'SedrRoomDeletionRequested',
      'SedrRoomDeletionApproved',
      'SedrRoomDeletionRejected',
      'SedrRoomPurged',
    ],
  },
];

export type AuditFilters = {
  module: AuditModule;
  eventType: string;
  actorUserId: string;
  from: string;
  to: string;
};

export function emptyAuditFilters(): AuditFilters {
  return { module: 'all', eventType: '', actorUserId: '', from: '', to: '' };
}

export function auditFiltersActive(filters: AuditFilters): boolean {
  return (
    filters.module !== 'all' ||
    Boolean(filters.eventType) ||
    Boolean(filters.actorUserId) ||
    Boolean(filters.from) ||
    Boolean(filters.to)
  );
}

export function readAuditFiltersFromUrl(search = window.location.search): AuditFilters {
  const params = new URLSearchParams(search);
  const module = params.get('module');
  return {
    module: AUDIT_MODULES.includes(module as AuditModule) ? (module as AuditModule) : 'all',
    eventType: params.get('eventType') ?? '',
    actorUserId: params.get('actor') ?? '',
    from: isoToDatetimeLocal(params.get('from') ?? ''),
    to: isoToDatetimeLocal(params.get('to') ?? ''),
  };
}

export function writeAuditFiltersToUrl(filters: AuditFilters): void {
  const params = new URLSearchParams(window.location.search);
  const setOrDelete = (key: string, value: string) => {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  };
  setOrDelete('module', filters.module === 'all' ? '' : filters.module);
  setOrDelete('eventType', filters.eventType);
  setOrDelete('actor', filters.actorUserId);
  setOrDelete('from', datetimeLocalToIso(filters.from) ?? '');
  setOrDelete('to', datetimeLocalToIso(filters.to) ?? '');
  const query = params.toString();
  const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

export function toAuditQuery(filters: AuditFilters): ListAuditEventsParams {
  return {
    module: filters.module,
    eventType: filters.eventType || undefined,
    actorUserId: filters.actorUserId || undefined,
    from: datetimeLocalToIso(filters.from),
    to: datetimeLocalToIso(filters.to),
  };
}

export function datetimeLocalToIso(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function isoToDatetimeLocal(value: string): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function eventTypesForModule(module: AuditModule): string[] {
  if (module === 'all') {
    return AUDIT_EVENT_GROUPS.flatMap((group) => group.eventTypes);
  }
  return AUDIT_EVENT_GROUPS.find((group) => group.module === module)?.eventTypes ?? [];
}

export function buildAuditCsv(events: AuditEvent[], translate: Translate = t): string {
  const headers = [
    translate('admin.audit.csv.time'),
    translate('admin.audit.csv.action'),
    translate('admin.audit.csv.summary'),
    translate('admin.audit.csv.actor'),
    translate('admin.audit.csv.resource'),
    translate('admin.audit.csv.eventType'),
    translate('admin.audit.csv.actorUserId'),
    translate('admin.audit.csv.resourceType'),
    translate('admin.audit.csv.resourceId'),
  ];
  const rows = events.map((event) => [
    new Date(event.createdAt).toLocaleString(),
    tAuditEventType(event.eventType, translate),
    formatAuditSummary(event, translate),
    event.actorDisplay ?? '',
    event.resourceDisplay ?? event.resourceType ?? '',
    event.eventType,
    event.actorUserId ?? '',
    event.resourceType ?? '',
    event.resourceId ?? '',
  ]);
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export function downloadTextFile(fileName: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function auditCsvFileName(now = new Date()): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  return `audit-log-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`;
}

function csvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

export type Translate = (key: string, options?: Record<string, unknown>) => string;

type AuditPhrase =
  | 'created'
  | 'revoked'
  | 'removed'
  | 'purged'
  | 'invited'
  | 'updated'
  | 'verified'
  | 'failed'
  | 'sent'
  | 'invalidated'
  | 'replaced'
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'scheduled'
  | 'cancelled'
  | 'lockedOut'
  | 'passphrase'
  | 'digest'
  | 'imported'
  | 'extended';

const EVENT_PHRASES: Record<string, AuditPhrase> = {
  RecoveryPackageCreated: 'created',
  RecoveryPackageReplaced: 'replaced',
  RecoveryPackageAdminInvalidated: 'invalidated',
  VaultPassphraseChanged: 'passphrase',
  InfoShareCreated: 'created',
  InfoShareRevoked: 'revoked',
  InfoSharePurged: 'purged',
  InfoShareReplyPurged: 'purged',
  SharedFolderCreated: 'created',
  SharedFolderRevoked: 'revoked',
  SharedFolderExpiryExtended: 'extended',
  SharedFolderMemberInvited: 'invited',
  SharedFolderMemberPermissionChanged: 'updated',
  SharedFolderMemberRemoved: 'removed',
  SharedFolderGuestInviteCreated: 'invited',
  SharedFolderGuestInvitePermissionChanged: 'updated',
  SharedFolderGuestInviteRevoked: 'revoked',
  SharedFolderManifestCommitted: 'updated',
  SharedFolderGuestVerified: 'verified',
  GuestVerifyLockedOut: 'lockedOut',
  SharedFolderPurged: 'purged',
  SharedFolderExpiryWarningSent: 'sent',
  SharedFolderExpiryDigestSent: 'digest',
  SharedFolderDeletedFilePurged: 'purged',
  SharedFolderManifestHistoryPurged: 'purged',
  SmsSent: 'sent',
  SmsSendFailed: 'failed',
  SmsDeliveryFailed: 'failed',
  CmComBudgetAlertSent: 'sent',
  CmComBudgetAlertFailed: 'failed',
  SystemSettingsUpdated: 'updated',
  VersionUpdated: 'updated',
  VersionReportingFailed: 'failed',
  SedrRoomCreated: 'created',
  SedrMemberInvited: 'invited',
  SedrMemberRemoved: 'removed',
  SedrGuestInviteCreated: 'invited',
  SedrGuestInviteRevoked: 'revoked',
  SedrGuestVerified: 'verified',
  SedrRequirementsImported: 'imported',
  SedrRoomDeletionScheduled: 'scheduled',
  SedrRoomDeletionCancelled: 'cancelled',
  SedrRoomDeletionRequested: 'requested',
  SedrRoomDeletionApproved: 'approved',
  SedrRoomDeletionRejected: 'rejected',
  SedrRoomPurged: 'purged',
};

const DANGER_EVENTS = new Set([
  'RecoveryPackageAdminInvalidated',
  'InfoShareRevoked',
  'InfoSharePurged',
  'InfoShareReplyPurged',
  'SharedFolderRevoked',
  'SharedFolderMemberRemoved',
  'SharedFolderGuestInviteRevoked',
  'SharedFolderPurged',
  'SharedFolderDeletedFilePurged',
  'SharedFolderManifestHistoryPurged',
  'SmsSendFailed',
  'CmComBudgetAlertFailed',
  'GuestVerifyLockedOut',
  'SedrMemberRemoved',
  'SedrGuestInviteRevoked',
  'SedrRoomDeletionRejected',
  'SedrRoomPurged',
]);

const WARNING_EVENTS = new Set([
  'RecoveryPackageReplaced',
  'VaultPassphraseChanged',
  'SharedFolderExpiryWarningSent',
  'CmComBudgetAlertSent',
  'SedrRoomDeletionScheduled',
  'SedrRoomDeletionRequested',
  'VersionReportingFailed',
]);

const SUCCESS_EVENTS = new Set([
  'RecoveryPackageCreated',
  'InfoShareCreated',
  'SharedFolderCreated',
  'SharedFolderExpiryExtended',
  'SharedFolderMemberInvited',
  'SharedFolderGuestInviteCreated',
  'SharedFolderGuestVerified',
  'SmsSent',
  'VersionUpdated',
  'SedrRoomCreated',
  'SedrMemberInvited',
  'SedrGuestInviteCreated',
  'SedrGuestVerified',
  'SedrRoomDeletionApproved',
  'SedrRoomDeletionCancelled',
]);

const unescape = { interpolation: { escapeValue: false } };

export function tAuditEventType(eventType: string, translate: Translate = t): string {
  const key = `admin.audit.event.${eventType}`;
  const translated = translate(key);
  return translated === key ? eventType : translated;
}

export function auditEventBadgeClass(eventType: string): string {
  if (DANGER_EVENTS.has(eventType)) {
    return 'status-badge status-badge--danger audit-row__type';
  }
  if (WARNING_EVENTS.has(eventType)) {
    return 'status-badge status-badge--warning audit-row__type';
  }
  if (SUCCESS_EVENTS.has(eventType)) {
    return 'status-badge status-badge--success audit-row__type';
  }
  return 'status-badge audit-row__type';
}

export function formatAuditSummary(event: AuditEvent, translate: Translate = t): string {
  const details = parseDetails(event.detailsJson);
  const actor = resolveActor(event, translate);
  const resource = resolveResource(event, details, translate);
  const target = resolveTarget(event, details);
  const phrase = EVENT_PHRASES[event.eventType] ?? 'updated';
  const phraseKey = phraseKeyFor(phrase, target);
  const summary = translate(phraseKey, { actor, resource, target, ...unescape });
  const extras = formatAuditExtras(details, translate);
  return extras ? `${summary} · ${extras}` : summary;
}

export function formatAuditTooltip(event: AuditEvent): string {
  const parts: string[] = [];
  if (event.actorUserId) {
    parts.push(`actorUserId: ${event.actorUserId}`);
  }
  if (event.actorGuestSessionId) {
    parts.push(`actorGuestSessionId: ${event.actorGuestSessionId}`);
  }
  if (event.resourceType && event.resourceId) {
    parts.push(`${event.resourceType}: ${event.resourceId}`);
  }
  return parts.join(' · ');
}

function phraseKeyFor(phrase: AuditPhrase, target: string | null): string {
  if (phrase === 'invited' && !target) {
    return 'admin.audit.phrase.invitedNoTarget';
  }
  if (phrase === 'removed' && !target) {
    return 'admin.audit.phrase.removedNoTarget';
  }
  return `admin.audit.phrase.${phrase}`;
}

function resolveActor(event: AuditEvent, translate: Translate): string {
  if (event.actorDisplay) {
    return event.actorDisplay;
  }
  if (event.actorGuestSessionId) {
    return translate('admin.audit.guestActor');
  }
  if (event.actorUserId) {
    return translate('admin.audit.unknownActor');
  }
  return translate('admin.audit.systemActor');
}

function resolveResource(
  event: AuditEvent,
  details: Record<string, unknown> | null,
  translate: Translate,
): string {
  if (event.eventType === 'RecoveryPackageCreated' || event.eventType === 'RecoveryPackageReplaced') {
    return translate('admin.audit.resource.recoveryPackage');
  }

  if (
    event.eventType === 'SmsSent' ||
    event.eventType === 'SmsSendFailed' ||
    event.eventType === 'SmsDeliveryFailed' ||
    event.eventType === 'CmComBudgetAlertSent' ||
    event.eventType === 'CmComBudgetAlertFailed'
  ) {
    const destination = typeof details?.destinationMasked === 'string' ? details.destinationMasked : null;
    const destinations = Array.isArray(details?.destinationsMasked)
      ? details.destinationsMasked.filter((item): item is string => typeof item === 'string')
      : [];
    const name = destination ?? (destinations.length > 0 ? destinations.join(', ') : null);
    return name
      ? translate('admin.audit.resource.sms', { name, ...unescape })
      : translate('admin.audit.resource.sms.unnamed');
  }

  const type = event.resourceType ?? 'unknown';
  if (event.resourceDisplay) {
    const namedKey = `admin.audit.resource.${type}`;
    const named = translate(namedKey, { name: event.resourceDisplay, ...unescape });
    return named === namedKey ? event.resourceDisplay : named;
  }

  const unnamedKey = `admin.audit.resource.${type}.unnamed`;
  const unnamed = translate(unnamedKey);
  return unnamed === unnamedKey ? type : unnamed;
}

function resolveTarget(
  event: AuditEvent,
  details: Record<string, unknown> | null,
): string | null {
  if (event.targetDisplay) {
    return event.targetDisplay;
  }
  if (typeof details?.displayName === 'string' && details.displayName) {
    if (typeof details.organization === 'string' && details.organization) {
      return `${details.displayName} (${details.organization})`;
    }
    return details.displayName;
  }
  return null;
}

function formatAuditExtras(
  details: Record<string, unknown> | null,
  translate: Translate,
): string {
  if (!details) {
    return '';
  }

  const parts: string[] = [];
  if (typeof details.recoveryGeneration === 'number') {
    parts.push(`${translate('admin.audit.detail.recoveryGeneration')}: ${details.recoveryGeneration}`);
  }
  if (typeof details.passwordMode === 'string') {
    parts.push(`${translate('admin.audit.detail.passwordMode')}: ${details.passwordMode}`);
  }
  if (typeof details.multiUseAccess === 'boolean') {
    parts.push(
      `${translate('admin.audit.detail.multiUseAccess')}: ${
        details.multiUseAccess ? translate('info.owner.accessMulti') : translate('info.owner.accessSingle')
      }`,
    );
  }
  if (typeof details.permission === 'string' && details.permission) {
    parts.push(`${translate('admin.audit.detail.permission')}: ${details.permission}`);
  }
  if (typeof details.created === 'number' || typeof details.skipped === 'number') {
    parts.push(
      translate('admin.audit.detail.importSummary', {
        created: details.created ?? 0,
        skipped: details.skipped ?? 0,
      }),
    );
  }
  return parts.join(' · ');
}

function parseDetails(detailsJson: string | null): Record<string, unknown> | null {
  if (!detailsJson) {
    return null;
  }

  try {
    return JSON.parse(detailsJson) as Record<string, unknown>;
  } catch {
    return null;
  }
}
